import React from "react";
import { motion } from "framer-motion";
import { Sparkles, RotateCw, ThumbsUp, ThumbsDown, Search, Info, Lock } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { Card, Badge, Button } from "../../components/primitives";

// HRMS-only building blocks. Everything visual comes from the shared design
// tokens; this file only adds the shapes the HR pages need that the generic
// component set does not already cover.

// ── Layout helpers ──────────────────────────────────────────────────────────
export function Grid({ cols = 2, min = 260, gap, children, style }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: gap ?? t.s[4],
      ...style,
    }} data-cols={cols}>{children}</div>
  );
}

export function Stack({ gap, children, style }) {
  const t = useDashTheme();
  return <div style={{ display: "flex", flexDirection: "column", gap: gap ?? t.s[4], ...style }}>{children}</div>;
}

export function Row({ gap, align = "center", justify, wrap, children, style }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "flex", alignItems: align, justifyContent: justify, gap: gap ?? t.s[2],
      flexWrap: wrap ? "wrap" : "nowrap", minWidth: 0, ...style,
    }}>{children}</div>
  );
}

// ── AI surface ──────────────────────────────────────────────────────────────
// Token policy, enforced by the `cadence` prop so it is visible in the UI:
//   "batch"    — generated once server-side on a schedule and cached. Opening
//                the page costs nothing; the panel renders stored text.
//   "ondemand" — nothing runs until the user clicks. One call, one answer.
// There is deliberately no AI that fires on render.
export function AiPanel({ title, cadence = "batch", generatedAt, run, children, actions, dense }) {
  const t = useDashTheme();
  const [state, setState] = React.useState(run ? "idle" : "ready");
  const [output, setOutput] = React.useState(null);
  const [vote, setVote] = React.useState(null);

  const go = () => {
    setState("loading");
    setVote(null);
    // Stub for the real call. Replace with the AI service; the panel contract
    // (loading → text) does not change.
    setTimeout(() => { setOutput(run()); setState("ready"); }, 850);
  };

  const glow = t.isDark ? "rgba(120,159,232,0.35)" : "rgba(66,115,209,0.28)";

  return (
    <Card padding={0} style={{ position: "relative", overflow: "hidden", borderColor: t.c.border }}>
      <span style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 2, background: `linear-gradient(180deg, ${t.c.primary}, ${t.c.brand})` }} />
      <div style={{ padding: `${t.s[3]}px ${t.s[4]}px`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[3], borderBottom: `1px solid ${t.c.border}` }}>
        <Row gap={8}>
          <span style={{
            width: 22, height: 22, borderRadius: t.r.sm, display: "inline-flex", alignItems: "center",
            justifyContent: "center", background: t.c.primarySoft, color: t.c.primary,
            boxShadow: `0 0 0 3px ${glow}22`,
          }}><Sparkles size={13} /></span>
          <span style={{ fontSize: t.font.size.lg, fontWeight: t.font.weight.medium, color: t.c.text }}>{title}</span>
          <Badge tone={cadence === "batch" ? "neutral" : "info"}>
            {cadence === "batch" ? "Pre-generated" : "Runs on click"}
          </Badge>
        </Row>
        <Row gap={6}>
          {actions}
          {run && state === "ready" && (
            <button title="Regenerate" onClick={go} style={iconBtn(t)}><RotateCw size={13} /></button>
          )}
        </Row>
      </div>

      <div style={{ padding: dense ? t.s[3] : t.s[4] }}>
        {state === "idle" && (
          <Row justify="space-between" gap={12} wrap>
            <span style={{ fontSize: t.font.size.md, color: t.c.textSecondary, maxWidth: 520 }}>
              Nothing has been generated yet. This costs one model call and only runs when you ask.
            </span>
            <Button variant="primary" size="sm" leftIcon={<Sparkles size={13} />} onClick={go}>Generate</Button>
          </Row>
        )}
        {state === "loading" && <AiSkeleton />}
        {state === "ready" && (output || children)}
      </div>

      {state === "ready" && (output || children) && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[3], flexWrap: "wrap",
          padding: `${t.s[2]}px ${t.s[4]}px`, borderTop: `1px solid ${t.c.border}`, background: t.c.surfaceMuted,
        }}>
          <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>
            AI-generated{generatedAt ? ` · ${generatedAt}` : ""} · figures link to source records · check before acting on it
          </span>
          <Row gap={4}>
            <button title="Helpful" onClick={() => setVote("up")} style={iconBtn(t, vote === "up")}><ThumbsUp size={12} /></button>
            <button title="Not helpful" onClick={() => setVote("down")} style={iconBtn(t, vote === "down")}><ThumbsDown size={12} /></button>
          </Row>
        </div>
      )}
    </Card>
  );
}

function AiSkeleton() {
  const t = useDashTheme();
  const bar = (w, d) => (
    <motion.span
      initial={{ opacity: 0.35 }} animate={{ opacity: [0.35, 0.75, 0.35] }}
      transition={{ duration: 1.3, repeat: Infinity, delay: d }}
      style={{ display: "block", height: 10, width: w, borderRadius: 5, background: t.c.surfaceHover }}
    />
  );
  return <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>{bar("62%", 0)}{bar("94%", 0.1)}{bar("78%", 0.2)}</div>;
}

const iconBtn = (t, active) => ({
  width: 26, height: 26, display: "inline-flex", alignItems: "center", justifyContent: "center",
  borderRadius: t.r.xs, border: `1px solid ${active ? t.c.primary : "transparent"}`,
  background: active ? t.c.primarySoft : "transparent",
  color: active ? t.c.primary : t.c.textTertiary, cursor: "pointer",
});

// Bulleted AI body with an optional evidence chip per line.
export function AiLines({ items }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: t.s[2] }}>
      {items.map((it, i) => (
        <Row key={i} align="flex-start" gap={8}>
          <span style={{ width: 5, height: 5, borderRadius: 99, background: t.c.primary, marginTop: 7, flexShrink: 0 }} />
          <span style={{ fontSize: t.font.size.base, color: t.c.text, lineHeight: "20px" }}>
            {it.text}
            {it.evidence && (
              <span style={{ marginLeft: 6, fontSize: t.font.size.xs, color: t.c.textTertiary }}>({it.evidence})</span>
            )}
          </span>
        </Row>
      ))}
    </div>
  );
}

// ── Key/value display ────────────────────────────────────────────────────────
export function KV({ label, value, mono, locked, action }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <Row gap={5}>
        <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary }}>{label}</span>
        {locked && <Lock size={10} style={{ color: t.c.textTertiary }} />}
      </Row>
      <Row gap={8} justify="space-between">
        <span style={{
          fontSize: t.font.size.base, color: t.c.text,
          fontVariantNumeric: mono ? "tabular-nums" : "normal",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{value || "—"}</span>
        {action}
      </Row>
    </div>
  );
}

export function KVGrid({ items, min = 200 }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: `${t.s[4]}px ${t.s[5]}px` }}>
      {items.map((it) => <KV key={it.label} {...it} />)}
    </div>
  );
}

// ── Bits ─────────────────────────────────────────────────────────────────────
export function Meter({ value, max = 100, tone = "brand", height = 6 }) {
  const t = useDashTheme();
  const map = { brand: t.c.primary, positive: t.status.positive, warning: t.status.warning, negative: t.status.negative, info: t.status.info };
  const pct = Math.max(0, Math.min(100, (value / (max || 1)) * 100));
  return (
    <div style={{ width: "100%", height, borderRadius: 99, background: t.c.surfaceHover, overflow: "hidden" }}>
      <motion.div
        initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.7, ease: t.motion.ease.out }}
        style={{ height: "100%", borderRadius: 99, background: map[tone] || map.brand }}
      />
    </div>
  );
}

// Amber before → after cell. The one pattern that makes an approval queue safe:
// the approver sees exactly what changes, not just that something changed.
export function Diff({ from, to }) {
  const t = useDashTheme();
  return (
    <Row gap={6} wrap>
      <span style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, textDecoration: "line-through" }}>{from || "empty"}</span>
      <span style={{ color: t.c.textTertiary, fontSize: t.font.size.sm }}>→</span>
      <span style={{
        fontSize: t.font.size.sm, color: t.status.warning, background: t.status.warningSoft,
        padding: "1px 6px", borderRadius: t.r.xs, fontWeight: t.font.weight.medium,
      }}>{to}</span>
    </Row>
  );
}

export function SearchInput({ value, onChange, placeholder = "Search", width = 240 }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8, height: 36, width, padding: "0 10px",
      borderRadius: t.r.sm, border: `1px solid ${t.c.border}`, background: t.c.surface,
    }}>
      <Search size={14} style={{ color: t.c.textTertiary, flexShrink: 0 }} />
      <input
        value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent", color: t.c.text, fontSize: t.font.size.md, fontFamily: t.font.family }}
      />
    </div>
  );
}

export function Toggle({ checked, onChange, disabled }) {
  const t = useDashTheme();
  return (
    <button
      role="switch" aria-checked={!!checked} disabled={disabled}
      onClick={() => onChange?.(!checked)}
      style={{
        width: 36, height: 20, borderRadius: 99, border: "none", padding: 2, flexShrink: 0,
        background: checked ? t.c.primary : t.c.surfaceHover, cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1, display: "inline-flex", justifyContent: checked ? "flex-end" : "flex-start",
        transition: "background 160ms",
      }}
    >
      <motion.span layout transition={t.motion.spring.snappy}
        style={{ width: 16, height: 16, borderRadius: 99, background: "#fff", boxShadow: t.shadow.sm }} />
    </button>
  );
}

// One configuration line: what it is, what it does, and the control.
export function SettingRow({ title, desc, control, badge, last }) {
  const t = useDashTheme();
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: t.s[4],
      padding: `${t.s[3]}px ${t.s[4]}px`, borderBottom: last ? "none" : `1px solid ${t.c.border}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <Row gap={6}>
          <span style={{ fontSize: t.font.size.base, color: t.c.text }}>{title}</span>
          {badge}
        </Row>
        {desc && <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, lineHeight: "16px" }}>{desc}</div>}
      </div>
      <div style={{ flexShrink: 0 }}>{control}</div>
    </div>
  );
}

// Deterministic rule output — no model involved. Kept visually distinct from
// AiPanel so nobody has to wonder whether a number was inferred or computed.
export function ChecksList({ items, onAction }) {
  const t = useDashTheme();
  const toneOf = { high: "negative", medium: "warning", low: "neutral" };
  return (
    <div>
      {items.map((it, i) => (
        <div key={it.title} style={{
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: t.s[3],
          padding: `${t.s[3]}px ${t.s[4]}px`,
          borderBottom: i === items.length - 1 ? "none" : `1px solid ${t.c.border}`,
        }}>
          <Row align="flex-start" gap={10}>
            <span style={{
              width: 6, height: 6, borderRadius: 99, marginTop: 6, flexShrink: 0,
              background: it.severity === "high" ? t.status.negative : it.severity === "medium" ? t.status.warning : t.status.neutral,
            }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{it.title}</div>
              <div style={{ fontSize: t.font.size.sm, color: t.c.textTertiary, marginTop: 2, lineHeight: "16px" }}>{it.detail}</div>
            </div>
          </Row>
          <Row gap={8}>
            <Badge tone={toneOf[it.severity]}>{it.severity}</Badge>
            {it.action && <Button size="sm" variant="ghost" onClick={() => onAction?.(it)}>{it.action}</Button>}
          </Row>
        </div>
      ))}
    </div>
  );
}

// Compact month grid. Marks: { "2026-08-15": { tone, label } }
export function MiniMonth({ year, month, marks = {}, onPick }) {
  const t = useDashTheme();
  const first = new Date(year, month, 1);
  const days = new Date(year, month + 1, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Monday-first
  const cells = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  const toneMap = { holiday: t.status.info, leave: t.status.warning, off: t.c.textTertiary, event: t.status.positive };
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 6 }}>
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
          <div key={i} style={{ textAlign: "center", fontSize: t.font.size.xs, color: t.c.textTertiary }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} />;
          const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const mark = marks[key];
          return (
            <button
              key={key} onClick={() => onPick?.(key, mark)} title={mark?.label}
              style={{
                height: 30, borderRadius: t.r.xs, border: isToday(d) ? `1px solid ${t.c.primary}` : "1px solid transparent",
                background: mark ? `${toneMap[mark.tone] || t.c.primary}1F` : "transparent",
                color: mark ? toneMap[mark.tone] || t.c.primary : t.c.textSecondary,
                fontSize: t.font.size.sm, fontFamily: t.font.family, cursor: "pointer",
                fontVariantNumeric: "tabular-nums", position: "relative",
              }}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Legend({ items }) {
  const t = useDashTheme();
  return (
    <Row gap={12} wrap>
      {items.map((it) => (
        <Row key={it.label} gap={5}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: it.color }} />
          <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary }}>{it.label}</span>
        </Row>
      ))}
    </Row>
  );
}

// Small inline explainer for rules that would otherwise be invisible.
export function Hint({ children }) {
  const t = useDashTheme();
  return (
    <Row align="flex-start" gap={7} style={{
      padding: `${t.s[2]}px ${t.s[3]}px`, borderRadius: t.r.sm,
      background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`,
    }}>
      <Info size={13} style={{ color: t.c.textTertiary, flexShrink: 0, marginTop: 2 }} />
      <span style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, lineHeight: "17px" }}>{children}</span>
    </Row>
  );
}

// Vertical event timeline (change history, approval chains).
export function Timeline({ items }) {
  const t = useDashTheme();
  return (
    <div style={{ position: "relative", paddingLeft: 18 }}>
      <span style={{ position: "absolute", left: 4, top: 6, bottom: 6, width: 1, background: t.c.border }} />
      <Stack gap={t.s[4]}>
        {items.map((it, i) => (
          <div key={i} style={{ position: "relative" }}>
            <span style={{
              position: "absolute", left: -18, top: 5, width: 9, height: 9, borderRadius: 99,
              background: t.c.surface, border: `2px solid ${i === 0 ? t.c.primary : t.c.borderStrong}`,
            }} />
            <div style={{ fontSize: t.font.size.base, color: t.c.text }}>{it.what}</div>
            <div style={{ fontSize: t.font.size.sm, color: t.c.textSecondary, marginTop: 2 }}>{it.detail}</div>
            <div style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, marginTop: 3 }}>{it.when} · {it.by}</div>
          </div>
        ))}
      </Stack>
    </div>
  );
}

// Secondary tab strip used inside drawers and section bodies.
export function PillTabs({ tabs, value, onChange }) {
  const t = useDashTheme();
  return (
    <div style={{ display: "flex", gap: 4, overflowX: "auto", paddingBottom: 2 }}>
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key} onClick={() => onChange(tab.key)}
            style={{
              height: 28, padding: "0 10px", borderRadius: t.r.sm, whiteSpace: "nowrap",
              border: `1px solid ${active ? "transparent" : t.c.border}`,
              background: active ? t.c.primarySoft : "transparent",
              color: active ? t.c.primary : t.c.textSecondary,
              fontSize: t.font.size.sm, fontWeight: active ? 500 : 400,
              fontFamily: t.font.family, cursor: "pointer", flexShrink: 0,
            }}
          >{tab.label}</button>
        );
      })}
    </div>
  );
}

export function StatusBadge({ value }) {
  const map = {
    Active: "positive", Approved: "positive", Paid: "positive", Verified: "positive",
    Present: "positive", Allocated: "positive", Resolved: "positive", Healthy: "positive", Closed: "neutral",
    Pending: "warning", Probation: "warning", "Pending review": "warning", "On leave": "info",
    Open: "info", "Notice period": "warning", "Missing punch": "negative", Rejected: "negative",
    "Not submitted": "neutral", Exited: "neutral",
  };
  return <Badge tone={map[value] || "neutral"} dot>{value}</Badge>;
}